import {
  FirebaseOptions,
  getApp,
  getApps,
  initializeApp,
  FirebaseApp,
} from 'firebase/app';
import { getAnalytics, logEvent } from 'firebase/analytics';
import {
  GoogleAuthProvider,
  OAuthProvider,
  initializeAuth,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  Auth,
  browserPopupRedirectResolver,
} from 'firebase/auth';
import { getFirebaseEnvConfig } from './utilities';
import { Analytics } from 'utilities/interfaces';

let firebaseAppInstance: FirebaseApp;
let firebaseAnalyticsInstance: Analytics;
let firebaseAuthInstance: Auth;
let firebaseGoogleAuthProviderInstance: GoogleAuthProvider;
let firebaseAppleAuthProviderInstance: OAuthProvider;

export const initializeFirebase = async () => {
  if (getApps().length === 0) {
    const firebaseEnvConfig = await getFirebaseEnvConfig();

    const firebaseConfig: FirebaseOptions = {
      apiKey: firebaseEnvConfig.firebaseApiKey,
      authDomain: firebaseEnvConfig.firebaseAuthDomain,
      appId: firebaseEnvConfig.firebaseAppId,
      projectId: firebaseEnvConfig.firebaseProjectId,
      storageBucket: firebaseEnvConfig.firebaseStorageBucket,
      messagingSenderId: firebaseEnvConfig.firebaseMessagingSenderId,
      measurementId: firebaseEnvConfig.firebaseMeasurementId,
    };

    firebaseAppInstance = initializeApp(firebaseConfig);
  } else {
    firebaseAppInstance = getApp();
  }

  firebaseAnalyticsInstance = {
    logEvent: (eventName, eventData) => {
      logEvent(getAnalytics(firebaseAppInstance), eventName, eventData);
    },
  };

  firebaseAuthInstance = initializeAuth(firebaseAppInstance, {
    persistence: [browserLocalPersistence, indexedDBLocalPersistence],
    popupRedirectResolver: browserPopupRedirectResolver,
  });

  const environment = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV;
  if (environment === 'staging') {
    firebaseAuthInstance.settings.appVerificationDisabledForTesting = true;
  }

  firebaseGoogleAuthProviderInstance = new GoogleAuthProvider();
  firebaseGoogleAuthProviderInstance.addScope('email');

  firebaseAppleAuthProviderInstance = new OAuthProvider('apple.com');
  firebaseAppleAuthProviderInstance.addScope('email');
  firebaseAppleAuthProviderInstance.addScope('name');
};

export const getFirebaseAnalytics = () => {
  if (!firebaseAnalyticsInstance)
    throw new Error(
      'Firebase Analytics not initialized. Call initializeFirebase first.',
    );
  return firebaseAnalyticsInstance;
};

export const getFirebaseAuth = () => {
  if (!firebaseAuthInstance)
    throw new Error(
      'Firebase Auth not initialized. Call initializeFirebase first.',
    );
  return firebaseAuthInstance;
};

export const getFirebaseGoogleAuthProvider = () => {
  if (!firebaseGoogleAuthProviderInstance)
    throw new Error(
      'Firebase Google Auth Provider not initialized. Call initializeFirebase first.',
    );
  return firebaseGoogleAuthProviderInstance;
};

export const getFirebaseAppleAuthProvider = () => {
  if (!firebaseAppleAuthProviderInstance)
    throw new Error(
      'Firebase Apple Auth Provider not initialized. Call initializeFirebase first.',
    );
  return firebaseAppleAuthProviderInstance;
};
